import React, { useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  Spin,
  Table,
  Typography,
  ConfigProvider,
  Space,
  Modal,
} from 'antd';
import { FaSearch, FaInfoCircle } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';
import formatarValor from '../formatarValor';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PedidosCancelados = () => {
  const [loading, setLoading] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [dates, setDates] = useState([null, null]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [modalVisible, setModalVisible] = useState(false);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);

  const primaryColor = 'rgb(249, 130, 68)';
  const secondaryColor = 'rgb(247, 72, 115)';
  const gradient = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;

  useEffect(() => {
    document.title = 'Pedidos Cancelados';
  }, []);

  const fetchPedidos = async () => {
    if (!dates[0] || !dates[1]) {
      toast.error('Selecione a data de início e fim.');
      return;
    }

    try {
      setLoading(true);
      const data = await Api.ListPedidoCancelado({
        dataInicio: dates[0].format('YYYY-MM-DD'),
        dataFim: dates[1].format('YYYY-MM-DD'),
      });
      setPedidos(data.map((p, idx) => ({ ...p, key: idx })));
      setCurrentPage(1);
    } catch (error) {
      toast.error('Erro ao buscar pedidos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Funcionário',
      dataIndex: 'nome',
      key: 'nome',
      width: 220,
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 200,
    },
    {
      title: 'Produto',
      dataIndex: 'produto',
      key: 'produto',
      width: 200,
    },
    {
      title: 'Mesa',
      dataIndex: 'mesa',
      key: 'mesa',
      width: 100,
    },
    {
      title: 'Quantidade',
      dataIndex: 'quantidade',
      key: 'quantidade',
      align: 'center',
      width: 120,
    },
    {
      title: 'Preço',
      dataIndex: 'preco',
      key: 'preco',
      render: (v) => formatarValor(Number(v)),
      align: 'right',
      width: 120,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (v) => <strong>{formatarValor(Number(v))}</strong>,
      align: 'right',
      width: 130,
    },
    {
      title: 'Data',
      dataIndex: 'data',
      key: 'data',
      width: 140,
      render: (value) => dayjs(value).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 100,
      render: (_, record) => (
        <Button
          icon={<FaInfoCircle />}
          onClick={() => {
            setPedidoSelecionado(record);
            setModalVisible(true);
          }}
          style={{ color: primaryColor, borderColor: primaryColor }}
        />
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: primaryColor, borderRadius: 8 },
      }}
    >
      <div style={{ padding: 24, background: '#f8f9fa', minHeight: '100vh' }}>
        <ToastContainer position="top-right" />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 24,
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'center',
          }}
        >
          <Title
            level={2}
            style={{
              margin: 0,
              background: gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
            }}
          >
            PEDIDOS CANCELADOS
          </Title>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <RangePicker
              format="DD/MM/YYYY"
              value={dates}
              onChange={(vals) => setDates(vals)}
            />

            <Button
              type="primary"
              icon={<FaSearch />}
              onClick={fetchPedidos}
              style={{ background: gradient, border: 'none' }}
            >
              Filtrar
            </Button>
          </div>
        </div>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={pedidos}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: pedidos.length,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
            }}
            style={{ borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            locale={{ emptyText: 'Nenhum pedido encontrado' }}
          />
        </Spin>

        <Modal
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          title="Detalhes do Pedido Cancelado"
        >
          {pedidoSelecionado && (
            <div>
              <p><strong>Funcionário:</strong> {pedidoSelecionado.nome}</p>
              <p><strong>Cliente:</strong> {pedidoSelecionado.cliente}</p>
              <p><strong>Produto:</strong> {pedidoSelecionado.produto}</p>
              <p><strong>Mesa:</strong> {pedidoSelecionado.mesa}</p>
              <p><strong>Quantidade:</strong> {pedidoSelecionado.quantidade}</p>
              <p><strong>Preço:</strong> {formatarValor(pedidoSelecionado.preco)}</p>
              <p><strong>Total:</strong> {formatarValor(pedidoSelecionado.total)}</p>
              <p><strong>Data:</strong> {dayjs(pedidoSelecionado.data).format('DD/MM/YYYY HH:mm')}</p>
              <p style={{ marginTop: 12 }}><strong>Motivo do Cancelamento:</strong><br /> {pedidoSelecionado.motivo}</p>
            </div>
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default PedidosCancelados;
